import format from 'date-fns/format';

/**
 * Display a stored NAIVE wall-clock timestamp exactly as it was stored.
 *
 * Travel files (and documents) hold naive Israel wall clock in
 * `timestamp without time zone` columns. node-postgres parses such a value in
 * the server's zone (UTC) and JSON emits it with a `Z`, so a travel file opened
 * at 23:30 Israel time arrives as `"…T23:30:00.000Z"`. `new Date(value)` then
 * reads that as a UTC instant and a browser in Israel renders it as 02:30 the
 * NEXT day. The clock is therefore read straight out of the string, and any
 * `Z` / offset is ignored — the digits ARE the wall clock.
 *
 * Accepts `YYYY-MM-DD`, `YYYY-MM-DD HH:mm[:ss[.fff]]` and the ISO `T` form, with
 * or without a trailing `Z` / offset. A `Date` (or epoch number) is taken to have
 * come from parsing such a `Z` string, so its UTC fields are the wall clock.
 */

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?/;

/**
 * A browser-local `Date` carrying the stored wall clock's fields, so it can be
 * handed to date-fns `format`, a grid date filter or a date comparator and show
 * the same day and time that is stored. Returns null for empty or unparseable
 * input.
 */
export const parseWallClock = (value: unknown): Date | null => {
    if (value === null || value === undefined || value === '') return null;

    if (value instanceof Date || typeof value === 'number') {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return new Date(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            d.getUTCHours(),
            d.getUTCMinutes(),
            d.getUTCSeconds(),
            d.getUTCMilliseconds()
        );
    }

    const match = String(value).trim().match(WALL_CLOCK);
    if (!match) return null;
    const [, yyyy, mm, dd, hh = '0', mi = '0', ss = '0', frac = ''] = match;
    const ms = Number(frac.padEnd(3, '0').slice(0, 3));
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss), ms);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * `format` for a stored wall clock (date-fns tokens, e.g. `'dd/MM/yyyy'`,
 * `'MMM d, y'`, `'HH:mm:ss'`). Returns `emptyText` for empty or unparseable input.
 */
export const formatWallClock = (value: unknown, dateFormat: string, emptyText = ''): string => {
    const date = parseWallClock(value);
    return date ? format(date, dateFormat) : emptyText;
};
